'use client';

import type {
  ChcApprovalOddsConsent,
  ConsentAgreementItemInput,
} from '__generated__/graphql';
import type { ApprovalOddsResult } from './types';

export type ApprovalOddsEntry =
  | { status: 'loading'; generation: symbol }
  | { status: 'ready'; result: ApprovalOddsResult }
  | { status: 'error'; error: unknown };

const APPROVAL_ODDS_ENDPOINT = '/api/-cms/approval-odds';
const APPROVAL_ODDS_CONSENT_ENDPOINT = '/api/-cms/approval-odds-consent';

// `success` is validated in `sendConsent` (it throws when false), so it's
// stripped from the result consumers receive.
export type ApprovalOddsConsentResult = Omit<
  ChcApprovalOddsConsent['chcPostUserConsent'],
  'success'
>;

// Module-level store: shared across every consumer of `useApprovalOdds`
// in the SPA session. Keying by identity `sub` means a page with N
// ComparisonTable blocks fires one POST per identity, not one per block.
// The Maps are intentionally unbounded — entries live for the SPA session
// and are freed on full page navigation when this module is re-initialised.
const entries = new Map<string, Map<string, ApprovalOddsEntry>>();
const listeners = new Map<string, Map<string, Set<() => void>>>();

function getEntryByLanguage(
  sub: string,
  languageCode: string,
): ApprovalOddsEntry | undefined {
  return entries.get(sub)?.get(languageCode);
}

function setEntryByLanguage(
  sub: string,
  languageCode: string,
  entry: ApprovalOddsEntry,
): void {
  let byLanguage = entries.get(sub);
  if (!byLanguage) {
    byLanguage = new Map();
    entries.set(sub, byLanguage);
  }
  byLanguage.set(languageCode, entry);
}

function deleteEntryByLanguage(sub: string, languageCode: string): void {
  const byLanguage = entries.get(sub);
  if (!byLanguage) return;
  byLanguage.delete(languageCode);
  if (byLanguage.size === 0) {
    entries.delete(sub);
  }
}

function getListenersByLanguage(
  sub: string,
  languageCode: string,
): Set<() => void> | undefined {
  return listeners.get(sub)?.get(languageCode);
}

function notify(sub: string, languageCode: string): void {
  const subs = getListenersByLanguage(sub, languageCode);
  if (!subs) return;
  for (const fn of subs) {
    fn();
  }
}

export function getEntry(
  sub: string,
  languageCode: string,
): ApprovalOddsEntry | undefined {
  return getEntryByLanguage(sub, languageCode);
}

function startFetch(sub: string, languageCode: string): void {
  // Per-fetch loading sentinel. Result is only committed if the current
  // entry is still this exact sentinel — otherwise an invalidate()/refresh()
  // raced ahead and a newer fetch owns the slot.
  //
  // Mint a fresh sentinel when there is no entry, or when the current entry
  // is itself an in-flight loading sentinel: replacing it supersedes the
  // older fetch so its (possibly failed) result can't clobber this one and
  // leave consumers stuck in `error` after a successful retry. A ready/error
  // entry is reused as the sentinel and kept visible for a background refresh
  // (e.g. the optimistic consent entry written before invalidate()).
  const current = getEntryByLanguage(sub, languageCode);
  const loading: ApprovalOddsEntry =
    !current || current.status === 'loading'
      ? { status: 'loading', generation: Symbol('approval-odds-fetch') }
      : current;

  if (loading !== current) {
    setEntryByLanguage(sub, languageCode, loading);
  }

  void (async () => {
    let owned = false;
    try {
      const res = await fetch(APPROVAL_ODDS_ENDPOINT, {
        method: 'POST',
        body: '{}',
        headers: {
          'accept-language': languageCode,
          'content-type': 'application/json',
        },
      });
      if (getEntryByLanguage(sub, languageCode) !== loading) return; // superseded
      if (!res.ok) {
        setEntryByLanguage(sub, languageCode, {
          status: 'error',
          error: new Error(`http ${res.status}`),
        });
        owned = true;
        return;
      }
      const result = (await res.json()) as ApprovalOddsResult;

      // Re-check after awaited json(): another invalidate could have
      // run while we were parsing the body.
      if (getEntryByLanguage(sub, languageCode) !== loading) return;
      setEntryByLanguage(sub, languageCode, { status: 'ready', result });
      owned = true;
    } catch (error) {
      if (getEntryByLanguage(sub, languageCode) !== loading) return; // superseded
      setEntryByLanguage(sub, languageCode, { status: 'error', error });
      owned = true;
    } finally {
      // Only notify when we actually wrote a terminal state — a foreign
      // newer fetch will run its own notify when it completes.
      if (owned) notify(sub, languageCode);
    }
  })();
}

export function ensureFetch(sub: string, languageCode: string): void {
  const existing = getEntryByLanguage(sub, languageCode);
  // Reuse any existing entry — loading, ready, or error. Error retry is
  // an explicit invalidate()/refresh().
  if (existing) return;

  startFetch(sub, languageCode);

  // Notify synchronously so consumers' loading-state callbacks fire
  // deterministically before the fetch's microtask resolves.
  notify(sub, languageCode);
}

export function subscribe(
  sub: string,
  languageCode: string,
  fn: () => void,
): () => void {
  let listenersByLanguage = listeners.get(sub);
  if (!listenersByLanguage) {
    listenersByLanguage = new Map();
    listeners.set(sub, listenersByLanguage);
  }

  let subs = listenersByLanguage.get(languageCode);
  if (!subs) {
    subs = new Set();
    listenersByLanguage.set(languageCode, subs);
  }

  subs.add(fn);

  return () => {
    const targetSubs = listeners.get(sub)?.get(languageCode);
    if (!targetSubs) return;

    targetSubs.delete(fn);
    if (targetSubs.size > 0) return;

    const byLanguage = listeners.get(sub);
    byLanguage?.delete(languageCode);
    if ((byLanguage?.size ?? 0) === 0) {
      listeners.delete(sub);
    }
  };
}

export function invalidate(sub: string, languageCode: string): void {
  const hasListeners =
    (getListenersByLanguage(sub, languageCode)?.size ?? 0) > 0;
  if (hasListeners) {
    // Refresh in the background. A ready/error entry is kept visible while
    // the refetch runs; an in-flight loading entry is replaced with a fresh
    // sentinel inside startFetch() so the superseded fetch can't clobber it.
    startFetch(sub, languageCode);
    notify(sub, languageCode);
    return;
  }
  deleteEntryByLanguage(sub, languageCode);
  notify(sub, languageCode);
}

function applyOptimisticConsent(
  sub: string,
  languageCode: string,
  consentAgreements: ReadonlyArray<ConsentAgreementItemInput>,
): void {
  const entry = getEntryByLanguage(sub, languageCode);
  if (!entry || entry.status !== 'ready') return;

  const consentedAt = new Date().toISOString();
  const consents = [...entry.result.chcUserConsents.consents];

  for (const agreement of consentAgreements) {
    const optimisticConsent = {
      agreementId: agreement.agreementId,
      agreementVersion: agreement.agreementVersion,
      // Match the upstream CHC status casing (see mocks.ts / API fixtures)
      // so optimistic entries stay consistent with refetched server data.
      status: 'ACCEPTED',
      consentedAt,
    };

    const existingIndex = consents.findIndex(
      (item) =>
        item.agreementId === agreement.agreementId &&
        item.agreementVersion === agreement.agreementVersion,
    );

    if (existingIndex >= 0) {
      consents[existingIndex] = optimisticConsent;
      continue;
    }

    consents.push(optimisticConsent);
  }

  setEntryByLanguage(sub, languageCode, {
    status: 'ready',
    result: {
      ...entry.result,
      chcUserConsents: {
        ...entry.result.chcUserConsents,
        consents,
      },
    },
  });
  notify(sub, languageCode);
}

export async function sendConsent(
  sub: string,
  languageCode: string,
  consentAgreements: ReadonlyArray<ConsentAgreementItemInput>,
): Promise<ApprovalOddsConsentResult> {
  const res = await fetch(APPROVAL_ODDS_CONSENT_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ input: { consentAgreements } }),
    headers: { 'content-type': 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`http ${res.status}`);
  }

  const { success, ...result } =
    (await res.json()) as ChcApprovalOddsConsent['chcPostUserConsent'];

  if (!success) {
    throw new Error('Consent update failed');
  }

  applyOptimisticConsent(sub, languageCode, consentAgreements);

  invalidate(sub, languageCode);

  return result;
}

// Test-only reset to avoid module state leaking between cases.
export function __resetStore(): void {
  entries.clear();
  listeners.clear();
}
